import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {Views} from '@ccp/config/views';
import {UrlParamLiterals} from '@ccp/enums/literals/urlparams.enum';
import {UrlManager} from '@ccp/lib/url-manager';
import {UserModel} from '@ccp/models/user.model';
import {Navigate} from '../lib/navigate';
import {NotificationTypes} from '../enums/notification-types.enum';
import {NotificationService} from '../services/notifications/notification.service';
import {NotificationMessage} from '@ccp/localization/notifications/notification-message';
import {DeviceDetectorService} from 'ngx-device-detector';
import {ProductContextService} from '@ccp/services/app-context/product-context.service';
import {UserContextService} from '@ccp/services/app-context/user-context.service';

let router: Router;
let productContextService: ProductContextService;
let userContextService: UserContextService;
let notificationService: NotificationService;
let deviceDetectorService: DeviceDetectorService;

export const OptinGuard: CanActivateFn = (route, state) => {
  productContextService = inject(ProductContextService);
  userContextService = inject(UserContextService);
  router = inject(Router);
  notificationService = inject(NotificationService);
  deviceDetectorService = inject(DeviceDetectorService);

  if (user().isLogged) {
    //CCP-21278 if user already logged product registered is empty but we have the hash in query params
    if (productContextService.isProductBeingRegistered$() || route.queryParamMap.get('hash')) {
      // if optout and existing account OR new customer
      if (!user().isOptedIn && !isSkipOptin(route)) {
        return true;
      }
      return goToNextStep(route);
    }
    return redirectToRegisterProduct(route);
  }
  return goToLogin(route, state);
};

/**
 * In case of user come directly from wechat or sso auto-login (including validation of token inside confirm email), we skip the optin page.
 * Same when user is already optin, we skip the optin page.
 */
function goToNextStep(route: ActivatedRouteSnapshot): boolean | UrlTree {
  if (isSkipOptin(route) || user().isOptedIn) {
    // Extract encoded params from return Url if user comes from login after confirming product
    const decodeReturnUrl = decodeURIComponent(UrlManager.getParam(UrlParamLiterals.ReturnUrl));
    const extractedParams = UrlManager.getParams(decodeReturnUrl);

    // remove from extractedParams
    if (UrlParamLiterals.noMatchError in extractedParams) {
      delete extractedParams[UrlParamLiterals.noMatchError];
    }

    let queryParams = {...route.queryParams};

    // if we are in POS flow E, we don't keep parameters while redirecting.
    // Here we are already logged.
    if (!extractedParams[UrlParamLiterals.PosRequestId]) {
      queryParams = {...queryParams, ...extractedParams};
    }
    return router.createUrlTree([Views.productRequestStatus.path], {queryParams: {...queryParams}});
  }
  return true;
}

function goToLogin(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree {
  return router.createUrlTree([Views.userLogin.path], {
    queryParams: {...route.queryParams, returnUrl: state.url},
  });
}

/**
 * Redirect to registration page and show an error message.
 * @returns {boolean}
 */
function redirectToRegisterProduct(route: ActivatedRouteSnapshot) {
  const isDesktop = deviceDetectorService.isDesktop();
  // avoid double redirection if desktop and lost of notification
  if (isDesktop) {
    Navigate.GoToProductManualRegistrationStatus(router, {queryParams: route.queryParams}, () =>
      notificationService.send(NotificationMessage['GENERIC_ERROR'], NotificationTypes.Error),
    );
  } else {
    Navigate.GoToProductQrCodeRegistration(router, {queryParams: route.queryParams}, () =>
      notificationService.send(NotificationMessage['GENERIC_ERROR'], NotificationTypes.Error),
    );
  }
  return false;
}

function getQueryParam(param: string): boolean {
  return param?.isBoolean() && param?.toBoolean();
}

function user(): UserModel {
  return userContextService.currentUser$();
}

function isSkipOptin(route: ActivatedRouteSnapshot): boolean {
  return getQueryParam(route.queryParams[UrlParamLiterals.SkipOptin]);
}
